import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Printer, ArrowRight, Quotes } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import Tier from "@/components/pricing/Tier";
import UpgradeCard from "@/components/pricing/UpgradeCard";
import {
  ProblemSection, ValueSection, RoiSection, ComparisonSection, FaqSection, ComplianceSection,
} from "@/components/pricing/sections";
import {
  CONTACT_EMAIL, TIER_TO_PACKAGE, MAIN_TIERS, PARTNER_TIERS, UPGRADES, PILOT_TERMS,
} from "@/lib/pricingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const handlePrint = () => window.print();

  const startCheckout = async (tierId) => {
    const pkg = TIER_TO_PACKAGE[tierId];
    if (!pkg) {
      // Enterprise = no package, open mail directly
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=Build%20A%20Bank%20%E2%80%94%20Firm%20License%20Inquiry`;
      return;
    }
    setCheckoutLoading(tierId);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error("Could not start checkout", {
        description: e?.response?.data?.detail || e.message,
      });
      setCheckoutLoading(null);
    }
  };

  const tierProps = (t) => ({
    ...t,
    onCta: () => startCheckout(t.id),
    loading: checkoutLoading === t.id,
  });

  return (
    <main data-testid="pricing-page" className="bg-brand-bone">
      <HeroSection onPrint={handlePrint} />
      <ProblemSection />
      <ValueSection />

      {/* ---------- PRICING CARDS ---------- */}
      <section id="pricing-cards" className="border-b border-brand-line bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="max-w-3xl mb-12">
            <span className="label-eyebrow">Advisor pricing</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
              Priced like a growth system,
              <br />
              not a basic calculator.
            </h2>
            <p className="mt-5 text-brand-ink/70 leading-relaxed">
              If Build A Bank helps you close even one additional IBC client every few months,
              the return outweighs the subscription many times over. Choose the plan that
              matches how aggressively you want to grow.
            </p>
          </div>

          {/* First row — the 3 ramp tiers (Launch → Ramp → Growth) */}
          <div className="grid md:grid-cols-3 gap-5 mb-5" data-testid="pricing-tiers">
            {MAIN_TIERS.map((t) => (
              <Tier key={t.id} {...tierProps(t)} />
            ))}
          </div>

          {/* Second row — Branded Pro, Territory Partner, Enterprise */}
          <div className="grid md:grid-cols-3 gap-5">
            {PARTNER_TIERS.map((t) => (
              <Tier key={t.id} {...tierProps(t)} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- OPTIONAL UPGRADES ---------- */}
      <section id="upgrades" className="border-b border-brand-line bg-brand-cream/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="max-w-3xl mb-12">
            <span className="label-eyebrow">Optional upgrades</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
              Add-ons for advisors who want
              <br />
              <span className="text-brand-copper">more brand or more market control.</span>
            </h2>
            <p className="mt-5 text-brand-ink/70 leading-relaxed">
              Stack these on top of any Launch, Ramp, or Growth plan when you want a lighter-touch
              white-label experience or category exclusivity — without committing to the full
              Branded Pro or Territory Partner agreements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5" data-testid="upgrade-tiers">
            {UPGRADES.map((u) => (
              <UpgradeCard key={u.id} {...u} />
            ))}
          </div>

          <p className="mt-8 text-xs italic text-brand-muted max-w-3xl">
            Optional upgrade pricing is illustrative and subject to availability. Final terms,
            exclusivity definitions, and minimum commitments are confirmed in your written
            agreement.
          </p>
        </div>
      </section>

      <PilotSection
        onStart={() => startCheckout("pilot")}
        loading={checkoutLoading === "pilot"}
      />
      <RoiSection />
      <ComparisonSection />
      <FaqSection />
      <ComplianceSection />
      <FinalCtaSection startCheckout={startCheckout} checkoutLoading={checkoutLoading} />
    </main>
  );
}

function HeroSection({ onPrint }) {
  return (
    <section className="relative overflow-hidden border-b border-brand-line">
      <div
        className="absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1708694557066-c9437fcfdc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ3JlZW4lMjBhYnN0cmFjdCUyMHRleHR1cmV8ZW58MHx8fHwxNzc3MDYyMjU5fDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: "cover",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-brand-bone via-brand-bone/95 to-brand-bone/50" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 no-print">
          <span className="label-eyebrow">For IBC-trained advisors</span>
          <Button
            variant="outline"
            onClick={onPrint}
            data-testid="btn-print-pricing"
            className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
          >
            <Printer size={16} weight="bold" /> Print this page
          </Button>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-forest leading-[1.04] max-w-4xl">
          Turn business cash-flow problems into{" "}
          <span className="italic text-brand-copper">IBC advisor opportunities.</span>
        </h1>
        <p className="mt-7 text-lg text-brand-ink/75 max-w-3xl leading-relaxed">
          Build A Bank helps IBC-trained advisors show business owners where money is leaking,
          what it could become inside a properly structured banking system, and why a real
          advisor conversation matters <em>now</em>.
        </p>
        <p className="mt-5 text-base text-brand-ink/70 max-w-3xl leading-relaxed">
          This is not another spreadsheet. It is a client-facing calculator, lead-capture
          funnel, advisor dashboard, and business-owner education tool — built for Infinite
          Banking conversations.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href="#pilot"
            data-testid="hero-cta-pilot"
            className="inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-6 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5"
          >
            Start advisor pilot <ArrowRight size={16} weight="bold" />
          </a>
          <a
            href="#pricing-cards"
            data-testid="hero-cta-pricing"
            className="inline-flex items-center gap-2 rounded-md border border-brand-forest/30 hover:border-brand-forest bg-white text-brand-forest px-6 py-3.5 text-sm font-semibold transition-colors"
          >
            See pricing
          </a>
        </div>

        <p className="mt-10 max-w-2xl text-sm italic text-brand-muted border-l-2 border-brand-copper pl-4">
          One qualified business-owner case can pay for the platform. One strong IBC client can
          pay for the year.
        </p>
      </div>
    </section>
  );
}

function PilotSection({ onStart, loading }) {
  return (
    <section id="pilot" className="border-b border-brand-line bg-brand-forest text-brand-bone grain relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <span className="text-[10px] uppercase tracking-[0.2em] text-brand-copper font-semibold">
            Try before you commit
          </span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-bone leading-tight">
            Want to see it before you commit?
          </h2>
          <p className="mt-5 text-brand-bone/80 text-lg max-w-2xl leading-relaxed">
            Start with a <strong className="text-brand-copper">$1 advisor pilot.</strong> Use
            it to explore the calculator, understand the advisor workflow, and see how the
            business-owner breakdown supports your IBC conversations.
          </p>
          <p className="mt-3 text-brand-bone/65 text-sm">
            After the pilot, choose the plan that fits your growth goals.
          </p>

          <button
            type="button"
            onClick={onStart}
            disabled={loading}
            data-testid="pilot-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-brand-copper hover:bg-brand-copperHover text-white px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            {loading ? "Opening checkout…" : "Start the $1 pilot"}
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>

        <div className="md:col-span-5">
          <div className="bg-brand-bone/10 border border-brand-bone/15 rounded-md p-6 backdrop-blur-sm">
            <div className="label-eyebrow text-brand-copper">Pilot terms</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {PILOT_TERMS.map((t) => (
                <li key={t} className="flex gap-2 items-start text-brand-bone/85">
                  <CheckCircle size={14} weight="fill" className="text-brand-copper mt-1 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCtaSection({ startCheckout, checkoutLoading }) {
  return (
    <section className="bg-brand-forest text-brand-bone grain relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 text-center">
        <Quotes size={36} weight="duotone" className="text-brand-copper mx-auto" />
        <h2 className="mt-5 font-serif text-4xl md:text-5xl text-brand-bone leading-tight max-w-3xl mx-auto">
          Ready to turn business cash-flow gaps
          <br />
          into advisor conversations?
        </h2>
        <p className="mt-6 text-brand-bone/75 max-w-2xl mx-auto text-lg leading-relaxed">
          Show the business owner the gap. Help them understand the opportunity. Then guide
          them into a properly structured strategy with you.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => startCheckout("pilot")}
            disabled={checkoutLoading === "pilot"}
            data-testid="final-cta-pilot"
            className="inline-flex items-center gap-2 rounded-md bg-brand-copper hover:bg-brand-copperHover text-white px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {checkoutLoading === "pilot" ? "Opening checkout…" : "Start advisor pilot"}
            <ArrowRight size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => startCheckout("growth")}
            disabled={checkoutLoading === "growth"}
            data-testid="final-cta-growth"
            className="inline-flex items-center gap-2 rounded-md border border-brand-bone/30 hover:bg-brand-bone/10 text-brand-bone px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {checkoutLoading === "growth" ? "Opening checkout…" : "Apply for Advisor Growth"}
          </button>
        </div>
        <p className="mt-12 text-xs text-brand-bone/45">
          Questions? Email{" "}
          <a className="text-brand-copper hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>
    </section>
  );
}
